'use client'

/**
 * Admin dashboard sign-in through the organisation's identity provider (identity integrations wave 2,
 * 27 Oct 2026), as the dashboard talks to the API.
 *
 * HumanFirewall is a security-awareness platform; this is the customer's own identity integration with the HF admin
 * dashboard. Three conversations:
 *   - sign-in (public): POST /auth/sso/initiate { email } -> the identity provider's URL; the provider comes back to
 *     the API, which sends the browser to /auth/sso/complete?handoff=<one-time code>; POST /auth/sso/exchange
 *     { code } answers exactly what a password login does (tokens, or the 2FA challenge);
 *   - the company's policy (Security page): GET / PUT /identity/sso/admin-policy;
 *   - refusals: every one carries an errorCode, worded here from the catalogue (identitySso.adminSignIn.errors.*,
 *     identitySso.policy.errors.*), never the API's English.
 *
 * Query keys follow the ESA pattern: no mode and no workspace id in a key (fetchApi's x-workspace-id header and
 * WorkspaceCacheGuard isolate tenants).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClientError, fetchApi } from '@/lib/api/client'
import type { LoginResponse } from '@/lib/api/auth'
import { normalizeTimeZone } from '@/lib/time/zone'

// ── Sign-in ─────────────────────────────────────────────────────────────────

export interface AdminSsoInitiateResponse {
  /** The identity provider's sign-in page (SAML AuthnRequest or OIDC authorize). */
  redirectUrl: string
  protocol: 'saml' | 'oidc'
}

/** What the exchange answers: a session, or the 2FA challenge, exactly as POST /auth/login. */
export type AdminSsoExchangeResponse = LoginResponse & { mustSetup2FA?: boolean }

/** The browser's current IANA zone (as on the password login), or undefined. */
function deviceTimeZone(): string | undefined {
  try {
    return normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone) ?? undefined
  } catch {
    return undefined
  }
}

/**
 * Where this tab keeps the browser binding between initiate and the exchange: a random value sent at both, so the
 * one-time code works only in the browser that started the sign-in (a code that reached another browser through a
 * link is worth nothing there). sessionStorage: per tab and per origin, and it survives the trip to the identity
 * provider and back.
 */
export const ADMIN_SSO_BINDING_KEY = 'hf-admin-sso-binding'

/** 32 random bytes, base64url (43 characters). */
export function newSsoBinding(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  let text = ''
  for (const b of bytes) text += String.fromCharCode(b)
  return btoa(text).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export const adminSsoApi = {
  initiate(email: string, binding: string): Promise<AdminSsoInitiateResponse> {
    return fetchApi<AdminSsoInitiateResponse>('/auth/sso/initiate', {
      method: 'POST',
      body: JSON.stringify({ email, binding }),
      skipAuth: true,
    })
  },

  exchange(code: string, binding: string): Promise<AdminSsoExchangeResponse> {
    return fetchApi<AdminSsoExchangeResponse>('/auth/sso/exchange', {
      method: 'POST',
      body: JSON.stringify({ code, binding, timezone: deviceTimeZone() }),
      skipAuth: true,
    })
  },
}

/** Leave for the identity provider (a full navigation; its own function so a test can watch it). */
export function goToIdentityProvider(url: string): void {
  window.location.assign(url)
}

/** Only an http(s) address is ever opened: the identity provider's page. */
export function isOpenableIdpUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch {
    return false
  }
}

const S = 'identitySso.adminSignIn.errors'

/** Every code an admin sign-in can be refused with, and the sentence for it. */
const SIGN_IN_KEYS: ReadonlyMap<string, string> = new Map([
  ['admin_sso_unavailable', `${S}.unavailable`],
  ['admin_sso_no_account', `${S}.noAccount`],
  ['admin_sso_expired', `${S}.expired`],
  ['sso_required', `${S}.ssoRequired`],
  ['portal_sso_expired', `${S}.attemptExpired`],
  ['portal_sso_failed', `${S}.failed`],
  ['portal_sso_not_configured', `${S}.notConfigured`],
  ['portal_sso_mfa_required', `${S}.mfaRequired`],
  ['portal_sso_email_missing', `${S}.emailMissing`],
  ['company_disabled', `${S}.companyDisabled`],
])

export const ADMIN_SIGN_IN_ERROR_CODES: readonly string[] = [...SIGN_IN_KEYS.keys()]

export const ADMIN_SIGN_IN_UNKNOWN_KEY = `${S}.unknown`

/** The sentence for a sign-in refusal code (`?sso_error=`, or an API errorCode); the generic one for any other. */
export function adminSignInErrorKey(code: string | null | undefined): string {
  return (code ? SIGN_IN_KEYS.get(code.trim()) : undefined) ?? ADMIN_SIGN_IN_UNKNOWN_KEY
}

/** The errorCode of a thrown API refusal, or null. */
export function errorCodeOf(error: unknown): string | null {
  return error instanceof ApiClientError ? error.errorCode : null
}

/** The company requires single sign-on and this sign-in used another method. */
export function isSsoRequired(error: unknown): boolean {
  return errorCodeOf(error) === 'sso_required'
}

// ── The company's policy (Settings -> Security) ─────────────────────────────

export type AdminSsoPolicyMode = 'allow' | 'require'
export type AdminSignsInWith = 'sso_only' | 'any_method' | 'emergency_owner'

export interface AdminSsoPolicyAdmin {
  id: string
  name: string
  email: string
  role: string
  isActive: boolean
  signsInWith: AdminSignsInWith
  /** ISO instant (Z), or null. */
  lastSignInAt: string | null
}

/** GET /identity/sso/admin-policy. */
export interface AdminSsoPolicy {
  mode: AdminSsoPolicyMode
  emergencyOwnerUserId: string | null
  updatedAt: string | null
  connections: Array<{ workspaceId: string; workspaceName: string; providerKey: string | null; protocol: 'saml' | 'oidc' }>
  requireAvailable: boolean
  needsEmergencyOwner: boolean
  owners: Array<{ id: string; name: string; email: string }>
  admins: AdminSsoPolicyAdmin[]
}

export const adminSsoPolicyKeys = {
  policy: () => ['identity', 'sso', 'admin-policy'] as const,
}

/** The policy; lists normalised here so the card never guards against a missing array. */
export function normalizeAdminSsoPolicy(raw: Partial<AdminSsoPolicy> | null | undefined): AdminSsoPolicy {
  return {
    mode: raw?.mode === 'require' ? 'require' : 'allow',
    emergencyOwnerUserId: raw?.emergencyOwnerUserId ?? null,
    updatedAt: raw?.updatedAt ?? null,
    connections: raw?.connections ?? [],
    requireAvailable: raw?.requireAvailable === true,
    needsEmergencyOwner: raw?.needsEmergencyOwner === true,
    owners: raw?.owners ?? [],
    admins: raw?.admins ?? [],
  }
}

export function useAdminSsoPolicy() {
  return useQuery({
    queryKey: adminSsoPolicyKeys.policy(),
    queryFn: () => fetchApi<AdminSsoPolicy>('/identity/sso/admin-policy'),
    select: normalizeAdminSsoPolicy,
    // A 403 (not the company tier) is an answer, not a blip.
    retry: (count, error) => !(error instanceof ApiClientError && error.statusCode === 403) && count < 2,
  })
}

export function useSaveAdminSsoPolicy() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { mode: AdminSsoPolicyMode; emergencyOwnerUserId: string | null }) =>
      fetchApi<AdminSsoPolicy>('/identity/sso/admin-policy', { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: (saved) => {
      queryClient.setQueryData(adminSsoPolicyKeys.policy(), saved)
      void queryClient.invalidateQueries({ queryKey: ['identity', 'sso'] })
    },
  })
}

const P = 'identitySso.policy.errors'

const POLICY_KEYS: ReadonlyMap<string, string> = new Map([
  ['admin_sso_policy_unusable', `${P}.unusable`],
  ['admin_sso_emergency_owner_invalid', `${P}.emergencyOwnerInvalid`],
  ['admin_sso_policy_forbidden', `${P}.forbidden`],
])

export const ADMIN_SSO_POLICY_ERROR_CODES: readonly string[] = [...POLICY_KEYS.keys()]

/** The sentence for a refused save (its errorCode), else the generic one. */
export function adminSsoPolicyErrorKey(error: unknown): string {
  const code = errorCodeOf(error)
  return (code ? POLICY_KEYS.get(code) : undefined) ?? `${P}.unknown`
}
